import { jsx as _jsx } from "react/jsx-runtime";
import { useTrack } from "@ssf/components";
import _ from "lodash";
import { Log, UserManager } from "oidc-client-ts";
import { createContext, useContext, useEffect, useState } from "react";
import { z } from "zod";
const authority = process.env.AUTH_URL;
if (!authority) {
    throw new Error("env `AUTH_URL` is not set");
}
const client_id = process.env.AUTH_CLIENT_ID;
if (!client_id) {
    throw new Error("env `AUTH_CLIENT_ID` is not set");
}
if (process.env.MODE === "local") {
    Log.setLevel(Log.DEBUG);
    Log.setLogger(console);
}
const location = typeof window !== "undefined"
    ? window.location
    : { protocol: "", host: "", pathname: "", href: "", search: "" };
const host = `${location.protocol}//${location.host}`;
export const callbackUrls = {
    redirect_uri: "/callback",
    silent_redirect_uri: "/silent_callback",
    post_logout_redirect_uri: "/logout_callback",
};
const callbackUrlsWithHost = Object.fromEntries(Object.entries(callbackUrls).map(([key, value]) => [key, host + value]));
export const userManager = new UserManager({
    authority,
    client_id,
    ...callbackUrlsWithHost,
    response_type: "code",
    scope: "openid offline_access email profile roles",
    response_mode: "query",
});
function isState(state) {
    return !!state && typeof state === "object" && "returnTo" in state;
}
(async () => {
    try {
        if (location.pathname === callbackUrls.redirect_uri) {
            const user = await userManager.signinCallback();
            const state = user?.state;
            const url = (isState(state) && state?.returnTo) || "/";
            location.href = url;
        }
        else if (location.pathname === callbackUrls.silent_redirect_uri) {
            await userManager.signinSilentCallback();
            throw new Error();
        }
        else if (location.pathname === callbackUrls.post_logout_redirect_uri) {
            // @ts-ignore
            const { state } = await userManager._client.readSignoutResponseState(location.href, true);
            const data = state?.data;
            const url = (isState(data) && data?.returnTo) || "/";
            location.href = url;
        }
    }
    catch (_error) {
        location.href = "/";
    }
})();
const AuthContext = createContext({
    isInitiated: false,
    isLoading: false,
    signinRedirect: userManager.signinRedirect,
    signinSilent: userManager.signinSilent,
    signoutRedirect: userManager.signoutRedirect,
});
export const AuthProvider = ({ onUser, signinRedirectArgs, signoutRedirectArgs, ...props }) => {
    const [state, setState] = useState({
        isLoading: false,
        isInitiated: false,
    });
    async function setLoading(fn) {
        setState({ ...state, isLoading: false });
        const value = await fn;
        setState({ ...state, isLoading: false });
        return value;
    }
    const setUser = (user) => {
        let validUser = user;
        if (user?.expired) {
            userManager.removeUser();
            validUser = null;
        }
        onUser?.(validUser);
        const isFedAdmin = getRoles(validUser).includes("federation:1");
        setState({ ...state, isInitiated: true, user: validUser, isFedAdmin });
        return validUser;
    };
    const signinRedirect = (args) => setLoading(userManager.signinRedirect(_.merge({ state: { returnTo: location.pathname + location.search } }, signinRedirectArgs, args)));
    const signoutRedirect = (args) => setLoading(userManager.signoutRedirect(_.merge({ state: { returnTo: location.pathname + location.search } }, signoutRedirectArgs, args)));
    const signinSilent = (args) => setLoading(userManager.signinSilent(args)).then(setUser);
    useEffect(() => {
        const userLoaded = (user) => {
            setUser(user);
        };
        userManager.events.addUserLoaded(userLoaded);
        userManager.getUser().then((user) => {
            if (!user) {
                return signinSilent().catch(() => setUser(null));
            }
            return setUser(user);
        });
        return () => {
            userManager.events.removeUserLoaded(userLoaded);
        };
    }, []);
    const { identify } = useTrack();
    useEffect(() => {
        if (state.isInitiated) {
            identify(state.user ? `memdb_${state.user.profile.sub}` : undefined);
        }
    }, [state.user, state.isInitiated]);
    return (_jsx(AuthContext.Provider, { value: { ...state, signinRedirect, signinSilent, signoutRedirect }, ...props }));
};
export const useAuth = () => useContext(AuthContext);
const rolesSchema = z.array(z.string());
export const getRoles = (user) => {
    const parsedRoles = rolesSchema.safeParse(user?.profile.roles);
    return parsedRoles.success ? parsedRoles.data : [];
};
//# sourceMappingURL=AuthProvider.js.map